import { inputFieldDefinitionsToVariables } from "@/domains/templates/mutations";
import { variableToInputFieldDefinition } from "@/domains/templates/input-fields";
import type {
  InputFieldDefinition,
  TemplateVariableInput,
  TemplateVariableTarget,
  Variable,
} from "@/domains/templates/types";

export type VariableHierarchyFailure = {
  sourceVariableId: string;
  name: string;
  error: unknown;
};

type SaveVariableHierarchyParams = {
  target: TemplateVariableTarget;
  draft: InputFieldDefinition;
  source?: Variable;
  existing: Variable[];
  loadChildren: (sourceVariableId: string) => Promise<Variable[]>;
  createVariable: (input: TemplateVariableInput) => Promise<Variable>;
  createHierarchy?: (input: TemplateVariableInput) => Promise<Variable[]>;
};

function definitionInput(
  definition: InputFieldDefinition,
): TemplateVariableInput {
  return inputFieldDefinitionsToVariables([definition])[0] ?? {};
}

function sourceInput(
  source: Variable,
  parentVariableId: string | null,
): TemplateVariableInput {
  return {
    ...definitionInput(variableToInputFieldDefinition(source)),
    source_variable_id: source.variable_id,
    parent_variable_id: parentVariableId,
  };
}

function appendUnique(variables: Variable[], variable: Variable): void {
  const index = variables.findIndex(
    (item) => item.variable_id === variable.variable_id,
  );
  if (index === -1) variables.push(variable);
  else variables[index] = variable;
}

export async function saveVariableHierarchy(
  params: SaveVariableHierarchyParams,
): Promise<{ saved: Variable[]; failed: VariableHierarchyFailure[] }> {
  void params.target;
  const saved = [...params.existing];
  const failed: VariableHierarchyFailure[] = [];
  const source = params.source;

  if (source?.has_children === true && params.createHierarchy) {
    try {
      const hierarchy = await params.createHierarchy({
        ...sourceInput(source, null),
        ...definitionInput(params.draft),
        source_variable_id: source.variable_id,
        parent_variable_id: null,
      });
      for (const variable of hierarchy) appendUnique(saved, variable);
    } catch (error) {
      failed.push({
        sourceVariableId: source.variable_id,
        name:
          params.draft.variableNameFrontend ??
          params.draft.key ??
          source.variable_name,
        error,
      });
    }
    return { saved, failed };
  }

  let scopedRoot = saved.find(
    (variable) =>
      variable.variable_id === params.draft.variableId ||
      (source &&
        variable.source_variable_id === source.variable_id &&
        variable.parent_variable_id === null),
  );

  if (!scopedRoot) {
    try {
      scopedRoot = await params.createVariable(
        source
          ? {
              ...sourceInput(source, null),
              ...definitionInput(params.draft),
              source_variable_id: source.variable_id,
              parent_variable_id: null,
            }
          : definitionInput(params.draft),
      );
      appendUnique(saved, scopedRoot);
    } catch (error) {
      failed.push({
        sourceVariableId: source?.variable_id ?? "custom",
        name:
          params.draft.variableNameFrontend ??
          params.draft.key ??
          source?.variable_name ??
          "Variable",
        error,
      });
      return { saved, failed };
    }
  }

  const repairChildren = async (
    catalogParent: Variable,
    scopedParent: Variable,
  ): Promise<void> => {
    if (catalogParent.has_children !== true) return;

    let catalogChildren: Variable[];
    try {
      catalogChildren = await params.loadChildren(catalogParent.variable_id);
    } catch (error) {
      failed.push({
        sourceVariableId: catalogParent.variable_id,
        name:
          catalogParent.variable_name_frontend ?? catalogParent.variable_name,
        error,
      });
      return;
    }

    for (const catalogChild of catalogChildren) {
      let scopedChild = saved.find(
        (variable) =>
          variable.source_variable_id === catalogChild.variable_id &&
          variable.parent_variable_id === scopedParent.variable_id,
      );
      if (!scopedChild) {
        try {
          scopedChild = await params.createVariable(
            sourceInput(catalogChild, scopedParent.variable_id),
          );
          appendUnique(saved, scopedChild);
        } catch (error) {
          failed.push({
            sourceVariableId: catalogChild.variable_id,
            name:
              catalogChild.variable_name_frontend ?? catalogChild.variable_name,
            error,
          });
          continue;
        }
      }
      await repairChildren(catalogChild, scopedChild);
    }
  };

  if (source) await repairChildren(source, scopedRoot);
  return { saved, failed };
}
